import { Button } from '@components/ui/button'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@components/ui/table'
import { AdasBox, AdasTypography } from '@components/wrapper-components'
import { Ban, Building2, Download, ExternalLink, Lock, Puzzle, Receipt, Rocket, SlidersHorizontal } from 'lucide-react'
import PropTypes from 'prop-types'
import { ADD_ON_BY_KEY, CONTACT_US, PORTAL } from '../../data/billingData'
import {
  discountLine,
  formatPrice,
  introLine,
  introLimitLine,
  introMaxPerBuyer,
  isTrialPackage,
  nextTierDown,
  nextTierUp,
  recommendedTier,
  tierById
} from '../../data/catalog'
import { Card, CollapsibleCard, DarkButton, OutlineButton, Pill, SectionIcon, StatusBanner, StripeWordmark } from './BillingUi'
import { PlanComparison } from './PlanComparison'
import { daysUntil, describePaymentMethod, formatBillingDate, toInvoiceRow, trialRefusalMessage } from './billingView'

// ── Reusable cards ──────────────────────────────────────────────────────────────────

// Stand-in when the subscription names a plan the catalog doesn't have (deleted row): the page
// still renders, without inventing a price.
const unknownTier = (id) => ({ id, name: 'Your plan', rank: 0, priceCents: null, tagline: '', highlights: [], perks: [], modules: [] })

/** "$1,799 / location / mo", or nothing when the plan has no price. */
const perLocation = (tier) => (tier.priceCents != null ? `${formatPrice(tier.priceCents)} / location / mo` : '')

// `title` / `priceLabel` let the trial states say what they are: during a free trial the
// customer is on this plan but not paying for it yet, so "Current Plan · $1,799/mo" would mislead.
// A light hero (plan, price, status, key date) with a primary accent bar.
const CurrentPlanCard = ({ tier, footer, title = 'Current Plan', priceLabel, action, payment, status }) => {
  const price = priceLabel ?? perLocation(tier)
  // "$2,200 / location / mo": the amount in full weight, the per-unit suffix lighter.
  const [amount, ...suffix] = price && price.startsWith('$') ? price.split(' / ') : [price]
  return (
    <Card className='overflow-hidden'>
      <AdasBox className='border-t-4 border-t-[#1F6FEB] bg-[linear-gradient(135deg,#F5F8FF_0%,#EAF1FF_100%)] px-5 py-5 sm:px-6'>
        <AdasBox className='flex flex-wrap items-center justify-between gap-3'>
          <AdasTypography className='text-[11px] font-bold uppercase tracking-wide text-[#1F6FEB]'>{title}</AdasTypography>
          {action}
        </AdasBox>
        <AdasBox className='mt-3 flex flex-wrap items-end justify-between gap-4'>
          <AdasBox className='flex min-w-0 items-start gap-3'>
            <AdasBox className='flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-[#1F6FEB]/10 text-[16px] font-bold text-[#1F6FEB] ring-1 ring-[#1F6FEB]/20'>
              A+
            </AdasBox>
            <AdasBox className='min-w-0'>
              <AdasBox className='flex flex-wrap items-center gap-2'>
                <AdasTypography className='text-[22px] font-bold leading-tight text-[#0A1628]'>{tier.name}</AdasTypography>
                {status && <Pill label={status.label} tone={status.tone} />}
              </AdasBox>
              {price && (
                <AdasTypography className='mt-0.5 text-[15px] font-semibold text-[#0A1628]'>
                  {amount}
                  {suffix.length > 0 && <span className='font-normal text-muted-foreground'> / {suffix.join(' / ')}</span>}
                </AdasTypography>
              )}
              {tier.tagline && (
                <AdasTypography className='mt-1 max-w-[560px] text-[13px] text-muted-foreground'>{tier.tagline}</AdasTypography>
              )}
            </AdasBox>
          </AdasBox>
          {footer}
        </AdasBox>
      </AdasBox>
      {/* One quiet strip for everything about paying: the card on file left, Stripe's attribution right. */}
      {payment && (
        <AdasBox className='flex flex-wrap items-center justify-between gap-x-6 gap-y-2 border-t border-[#E5E7EB] px-5 py-3 sm:px-6'>
          {payment}
          <AdasBox className='flex items-center gap-1.5'>
            <Lock className='h-3 w-3 text-muted-foreground' />
            <AdasTypography className='text-[13px] text-muted-foreground'>Payments managed securely by</AdasTypography>
            <StripeWordmark className='text-[13px]' />
          </AdasBox>
        </AdasBox>
      )}
    </Card>
  )
}
CurrentPlanCard.propTypes = {
  tier: PropTypes.object.isRequired,
  footer: PropTypes.node,
  title: PropTypes.string,
  priceLabel: PropTypes.string,
  /** Sits on the far right of the header (the "Compare plans" link). */
  action: PropTypes.node,
  /** The card on file (PaymentInline), shown in a footer strip with the "secured by Stripe" line. */
  payment: PropTypes.node,
  /** Status pill next to the plan name, e.g. `{ label: 'Active', tone: 'green' }`. */
  status: PropTypes.shape({ label: PropTypes.string.isRequired, tone: PropTypes.string })
}

// "Renews on / Trial ends" text on the right of the current-plan hero.
const PlanRenewalFooter = ({ label, value }) => (
  <AdasBox className='text-left sm:text-right'>
    <AdasTypography className='text-[12px] text-muted-foreground'>{label}</AdasTypography>
    <AdasTypography className='text-[15px] font-semibold text-[#0A1628]'>{value}</AdasTypography>
  </AdasBox>
)
PlanRenewalFooter.propTypes = { label: PropTypes.string.isRequired, value: PropTypes.string.isRequired }

// The card on file, in the current-plan card's footer strip: brand chip, "Visa ending in 4242" and the portal link.
// With no card on file only the link shows ("Add payment method").
const PaymentInline = ({ brand, last4, pending, onUpdate }) => {
  const description = describePaymentMethod({ pmBrand: brand, pmLast4: last4 })
  return (
    <AdasBox className='flex min-w-0 flex-wrap items-center gap-x-3 gap-y-1'>
      {description && (
        <AdasBox className='flex min-w-0 items-center gap-2'>
          <span className='inline-flex h-6 w-10 shrink-0 items-center justify-center rounded bg-[#172641] text-[9px] font-semibold uppercase tracking-wide text-white'>
            {brand || 'card'}
          </span>
          <AdasTypography className='truncate text-[13px] text-muted-foreground'>{description}</AdasTypography>
        </AdasBox>
      )}
      <button
        type='button'
        onClick={onUpdate}
        disabled={pending}
        className='inline-flex items-center gap-1.5 rounded text-[13px] font-medium text-[#2563EB] transition-colors hover:text-[#1D4ED8] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#2563EB]/30 disabled:cursor-not-allowed disabled:opacity-60'
      >
        {pending ? 'Opening…' : description ? PORTAL.label : PORTAL.addLabel}
        <ExternalLink className='h-3.5 w-3.5' />
      </button>
    </AdasBox>
  )
}
PaymentInline.propTypes = { brand: PropTypes.string, last4: PropTypes.string, pending: PropTypes.bool, onUpdate: PropTypes.func }

// Add-ons the subscription actually carries (none at MVP — hidden when empty).
const AddOnsCard = ({ addonKeys = [] }) => {
  if (!addonKeys.length) return null
  return (
    <CollapsibleCard title='Add-ons' icon={Puzzle} action={<Pill label={`${addonKeys.length} active`} tone='slate' dot={false} />}>
      <AdasBox className='flex flex-col gap-3 px-6 py-5'>
        {addonKeys.map((key) => {
          const a = ADD_ON_BY_KEY[key] || { name: key, note: '', priceLabel: '' }
          return (
            <AdasBox key={key} className='flex items-center justify-between gap-3'>
              <AdasBox className='flex items-center gap-2'>
                <AdasBox>
                  <AdasTypography className='text-[14px] font-semibold text-foreground'>{a.name}</AdasTypography>
                  {a.note && <AdasTypography className='text-[12px] text-muted-foreground'>{a.note}</AdasTypography>}
                </AdasBox>
                <Pill label='Active' tone='green' />
              </AdasBox>
              <AdasTypography className='text-[14px] font-semibold text-foreground'>{a.priceLabel}</AdasTypography>
            </AdasBox>
          )
        })}
      </AdasBox>
    </CollapsibleCard>
  )
}
AddOnsCard.propTypes = { addonKeys: PropTypes.arrayOf(PropTypes.string) }

const STATUS_TONE = { paid: 'green', open: 'amber', draft: 'slate', void: 'slate', uncollectible: 'red' }

// Starts collapsed in every state; the header shows how many invoices there are.
const InvoicesCard = ({ invoices, tierName }) => {
  const rows = (invoices || []).map((inv) => toInvoiceRow(inv, tierName))
  return (
    <CollapsibleCard
      title='Invoices'
      icon={Receipt}
      action={<Pill label={rows.length ? `Last ${rows.length}` : 'None yet'} tone='slate' dot={false} />}
    >
      {rows.length === 0 ? (
        <AdasBox className='flex flex-col items-center px-6 py-8 text-center'>
          <SectionIcon icon={Receipt} className='h-11 w-11 rounded-full [&>svg]:h-5 [&>svg]:w-5' />
          <AdasTypography className='mt-3 text-[13px] text-muted-foreground'>
            No invoices yet. Your first invoice appears after your first payment.
          </AdasTypography>
        </AdasBox>
      ) : (
        // The app's shared data table (components/ui/table): same header and row treatment as every other list.
        <Table className='min-w-[560px]'>
          <TableHeader>
            <TableRow className='hover:bg-transparent'>
              {['Date', 'Description', 'Total', 'Status', ''].map((h, i) => (
                <TableHead
                  key={h || i}
                  className={`px-4 first:pl-5 last:pr-5 sm:first:pl-6 sm:last:pr-6 ${h === 'Total' ? 'text-right' : ''}`}
                >
                  {h}
                </TableHead>
              ))}
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map((inv) => (
              <TableRow key={inv.key}>
                <TableCell className='whitespace-nowrap px-4 py-2.5 pl-5 text-foreground sm:pl-6'>{inv.date}</TableCell>
                <TableCell className='px-4 py-2.5 text-muted-foreground'>{inv.description}</TableCell>
                <TableCell className='whitespace-nowrap px-4 py-2.5 text-right font-medium text-foreground'>{inv.total}</TableCell>
                <TableCell className='px-4 py-2.5'>
                  <Pill label={inv.status} tone={STATUS_TONE[inv.status] || 'slate'} className='capitalize' />
                </TableCell>
                <TableCell className='px-4 py-2.5 pr-5 text-right sm:pr-6'>
                  {inv.href && (
                    <a
                      href={inv.href}
                      target='_blank'
                      rel='noreferrer'
                      className='inline-flex h-8 items-center gap-1.5 rounded-lg border border-[#1F6FEB]/40 px-3 text-[13px] font-semibold text-[#1F6FEB] transition-colors hover:bg-[#EAF2FE] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#1F6FEB]/40'
                    >
                      <Download className='h-3.5 w-3.5' aria-hidden='true' />
                      Download
                    </a>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </CollapsibleCard>
  )
}
InvoicesCard.propTypes = { invoices: PropTypes.array, tierName: PropTypes.string }

// A simple centered "empty / info" panel (no-subscription, network, canceled states).
const InfoPanel = ({ title, body, icon = Rocket, children }) => (
  <Card className='px-6 py-10 text-center sm:px-8'>
    <SectionIcon icon={icon} className='mx-auto mb-4 h-14 w-14 rounded-full [&>svg]:h-6 [&>svg]:w-6' />
    <AdasTypography className='text-[18px] font-bold text-foreground'>{title}</AdasTypography>
    <AdasTypography className='mx-auto mt-2 max-w-[520px] text-[14px] text-muted-foreground'>{body}</AdasTypography>
    <AdasBox className='mt-5 flex flex-wrap items-center justify-center gap-3'>{children}</AdasBox>
  </Card>
)
InfoPanel.propTypes = { title: PropTypes.string.isRequired, body: PropTypes.node, icon: PropTypes.elementType, children: PropTypes.node }

// "Want to change or cancel?" strip for the active state. Month-to-month: upgrade in-app,
// cancel in the billing portal, and downgrade by support request (our team makes that change).
// The card on file is updated from the current-plan card's footer, so it isn't repeated here.
const ManagePlanCard = ({ catalog, tier, pending, planChange, onManageBilling, onUpgrade, onDowngrade, onCancelDowngrade }) => {
  const up = nextTierUp(catalog, tier.id)
  const down = nextTierDown(catalog, tier.id)
  return (
    <AdasBox className='flex flex-col gap-3 rounded-xl border border-[#D1D5D8] bg-white px-5 py-4 shadow-sm md:flex-row md:items-center md:justify-between'>
      <AdasBox className='flex min-w-0 items-start gap-3 md:items-center'>
        <SectionIcon icon={SlidersHorizontal} />
        <AdasBox className='min-w-0'>
          <AdasTypography className='text-[14px] font-semibold text-foreground'>Want to change or cancel your plan?</AdasTypography>
          <AdasTypography className='text-[13px] text-muted-foreground'>
            No contract. Upgrades are prorated; downgrades and cancellations take effect at the end of your billing period.
          </AdasTypography>
        </AdasBox>
      </AdasBox>
      <AdasBox className='flex shrink-0 flex-wrap items-center gap-2'>
        {up && (
          <Button size='sm' onClick={() => onUpgrade?.(up.id)} disabled={pending} className='font-semibold'>
            {pending ? 'Working…' : `Upgrade to ${up.name}`}
          </Button>
        )}
        {/* One downgrade at a time: while it waits for our team the button is greyed out, with a way to stop it. */}
        {down && planChange?.status === 'PENDING' && (
          <>
            <Button size='sm' variant='outline' disabled>
              Downgrade in progress
            </Button>
            <Button size='sm' variant='outline' onClick={() => onCancelDowngrade?.(planChange.id)} disabled={pending}>
              Cancel downgrade
            </Button>
          </>
        )}
        {down && !planChange && (
          <Button size='sm' variant='outline' onClick={() => onDowngrade?.(down.id)}>
            Request downgrade
          </Button>
        )}
        <Button size='sm' variant='outline' onClick={onManageBilling} disabled={pending} className='text-muted-foreground'>
          {pending ? 'Opening…' : PORTAL.manageLabel}
        </Button>
      </AdasBox>
    </AdasBox>
  )
}
ManagePlanCard.propTypes = {
  catalog: PropTypes.object,
  tier: PropTypes.object.isRequired,
  pending: PropTypes.bool,
  onManageBilling: PropTypes.func,
  onUpgrade: PropTypes.func,
  onDowngrade: PropTypes.func,
  planChange: PropTypes.shape({ id: PropTypes.number, status: PropTypes.string }),
  onCancelDowngrade: PropTypes.func
}

// ── State-driven overview (PRD §6.2 — all six states, self-serve model) ──────────────
/**
 * Renders one of the six Billing states from the backend's SubscriptionView.
 * Self-serve: subscribe / reactivate / convert a trial → `onCheckout(tier)`; upgrade →
 * `onUpgrade(tier)`; update card or cancel → the Portal via `onManageBilling`, any time
 * (month-to-month); downgrade → `onDowngrade(tier)`, which opens a support request.
 * `onContact` remains for Network accounts.
 */
export const SubscriptionOverview = ({
  catalog,
  state,
  subscription,
  invoices,
  onCompare,
  onContact,
  onManageBilling,
  onCheckout,
  onUpgrade,
  onDowngrade,
  onCancelDowngrade,
  onEndTrial,
  pending
}) => {
  const sub = subscription || {}
  const tier = tierById(catalog, sub.tier) ?? (sub.tier ? unknownTier(sub.tier) : (recommendedTier(catalog) ?? unknownTier(null)))
  const stripeBacked = Boolean(sub.stripeBacked)
  // On (or just off) the Free Trial package: it is never bought again — what they buy is a monthly plan.
  const onTrialPackage = isTrialPackage(tier)
  const buyTier = onTrialPackage ? (recommendedTier(catalog) ?? tier) : tier

  // Solid primary-blue button (the kit's default Button: h-9, no wrap) on the right of the current-plan card header.
  const compareLink = (
    <Button size='sm' onClick={onCompare} className='font-semibold'>
      Compare plans
    </Button>
  )
  const paymentInline = <PaymentInline brand={sub.pmBrand} last4={sub.pmLast4} pending={pending} onUpdate={onManageBilling} />
  const comparisonProps = {
    catalog,
    hasStripeSubscription: stripeBacked,
    pending,
    downgradeInProgress: Boolean(sub.planChange),
    onCheckout,
    onUpgrade,
    onDowngrade
  }

  // 6 · Network account (QuickBooks) — read-only, no Stripe controls.
  if (state === 'network') {
    return (
      <AdasBox className='flex flex-col gap-4'>
        <StatusBanner tone='slate' label='Managed externally' title='Billing is managed by ADAS Intelligence.' />
        <InfoPanel
          icon={Building2}
          title='Billing for this location is managed by ADAS Intelligence'
          body='This is a Car ADAS+ Network account, billed through national agreements — not Stripe. To make changes, contact your account manager.'
        >
          <DarkButton onClick={onContact}>Contact your account manager</DarkButton>
        </InfoPanel>
      </AdasBox>
    )
  }

  // 1 · No subscription — self-serve signup: pick a plan and check out.
  if (state === 'none') {
    // Never subscribed (e.g. just signed up): the Free Trial is on offer too — once per location,
    // card and account. A refused (refunded) attempt is explained; one refused for the account
    // stops offering it.
    const refusal = trialRefusalMessage(sub.trialRefusal, introMaxPerBuyer(catalog))
    // The Free Trial package is retired: every plan carries its own intro period ("$1 for the first 30 days").
    const trial = undefined
    const lead = introLine(recommendedTier(catalog))
    const introLimit = introLimitLine(recommendedTier(catalog), introMaxPerBuyer(catalog))
    return (
      <AdasBox className='flex flex-col gap-4'>
        {refusal && <StatusBanner tone='amber' label='Intro period refunded' title={refusal} />}
        {introLimit && <StatusBanner label='Intro offer limit' title={introLimit} />}
        <InfoPanel
          title='Choose a package to get started'
          body={
            lead
              ? `Everything is locked until this location has a package. ${lead} on every plan, then the plan's monthly price — secure checkout by Stripe, billed per location, cancel any time.`
              : 'Everything is locked until this location has a package. Subscribe in a couple of minutes — secure checkout by Stripe, billed monthly per location, cancel any time. Need help deciding? We’re happy to talk it through.'
          }
        >
          {trial && (
            <DarkButton onClick={() => onCheckout?.(trial.id)} disabled={pending}>
              {pending ? 'Opening…' : `Start ${trial.durationDays}-day trial · ${formatPrice(trial.priceCents)}`}
            </DarkButton>
          )}
          {recommendedTier(catalog) &&
            (trial ? (
              <OutlineButton onClick={() => onCheckout?.(recommendedTier(catalog).id)} disabled={pending}>
                {`Subscribe to ${recommendedTier(catalog).name}`}
              </OutlineButton>
            ) : (
              <DarkButton onClick={() => onCheckout?.(recommendedTier(catalog).id)} disabled={pending}>
                {pending ? 'Opening…' : `Subscribe to ${recommendedTier(catalog).name}`}
              </DarkButton>
            ))}
          <OutlineButton onClick={onContact}>{CONTACT_US.label}</OutlineButton>
        </InfoPanel>
        <PlanComparison {...comparisonProps} />
      </AdasBox>
    )
  }

  // 5 · Canceled — access ended; reactivate through Checkout. No retention window (2026-10-13).
  if (state === 'canceled' && onTrialPackage) {
    return (
      <AdasBox className='flex flex-col gap-4'>
        <StatusBanner
          tone='red'
          label='Free Trial ended'
          title={`Your ${tier.name} ended on ${formatBillingDate(sub.canceledAt ?? sub.trialEndDate)}.`}
          body='Choose a plan to unlock everything again.'
          action={
            <DarkButton onClick={() => onCheckout?.(buyTier.id)} disabled={pending}>
              {pending ? 'Opening…' : `Subscribe to ${buyTier.name}`}
            </DarkButton>
          }
        />
        <PlanComparison {...comparisonProps} />
        <InvoicesCard invoices={invoices} tierName={tier.name} />
      </AdasBox>
    )
  }

  if (state === 'canceled') {
    return (
      <AdasBox className='flex flex-col gap-4'>
        <StatusBanner
          tone='slate'
          label='Canceled'
          title={`Your subscription ended on ${formatBillingDate(sub.canceledAt)}.`}
          body='Reactivate any time to pick up where you left off.'
        />
        <InfoPanel icon={Ban} title='Your subscription has ended' body='Reactivate to unlock this location again.'>
          <DarkButton onClick={() => onCheckout?.(buyTier.id)} disabled={pending}>
            {pending ? 'Opening…' : `Reactivate ${tier.name}`}
          </DarkButton>
          <OutlineButton onClick={onCompare}>Compare plans</OutlineButton>
        </InfoPanel>
        <InvoicesCard invoices={invoices} tierName={tier.name} />
      </AdasBox>
    )
  }

  // 4 · Past due — paused, only Billing reachable (no grace, 2026-10-13). Stripe-backed: a failed
  // charge, fix the card. In-app trial that expired (no Stripe subscription): subscribe to restore access.
  if (state === 'past_due') {
    return (
      <AdasBox className='flex flex-col gap-4'>
        {stripeBacked ? (
          <StatusBanner
            tone='red'
            label='Payment failed'
            title='Payment failed — this location is paused until the payment goes through.'
            body='Everything except Billing is locked. Update your card and access comes back as soon as the payment succeeds.'
            action={
              <DarkButton onClick={onManageBilling} disabled={pending}>
                {pending ? 'Opening…' : PORTAL.label}
              </DarkButton>
            }
          />
        ) : (
          <StatusBanner
            tone='red'
            label='Trial ended'
            title={`Your trial ended on ${formatBillingDate(sub.trialEndDate)}.`}
            body='This location is paused. Subscribe to unlock everything again — it only takes a minute.'
            action={
              <DarkButton onClick={() => onCheckout?.(buyTier.id)} disabled={pending}>
                {pending ? 'Opening…' : 'Subscribe to restore access'}
              </DarkButton>
            }
          />
        )}
        <CurrentPlanCard
          payment={stripeBacked && paymentInline}
          tier={tier}
          title={stripeBacked ? 'Current Plan' : 'Trial plan (ended)'}
          status={{ label: 'Paused', tone: 'red' }}
          action={stripeBacked ? undefined : compareLink}
          priceLabel={stripeBacked ? undefined : `${perLocation(tier) || 'Billed'} once you subscribe`}
          footer={
            <PlanRenewalFooter
              label={stripeBacked ? 'Was renewing' : 'Trial ended'}
              value={formatBillingDate(stripeBacked ? sub.currentPeriodEnd : sub.trialEndDate)}
            />
          }
        />
        {stripeBacked && <InvoicesCard invoices={invoices} tierName={tier.name} />}
      </AdasBox>
    )
  }

  // 2 · Trialing — day counter. Three sub-cases:
  //   in-app trial (no Stripe subscription): subscribe now → first month billed immediately, trial ends;
  //   Stripe-backed with a card on file (e.g. right after converting): nothing to do, say so;
  //   Stripe-backed without a card: add one before the trial ends.
  if (state === 'trialing' && onTrialPackage && !sub.trialPaused) {
    const daysLeft = daysUntil(sub.trialEndDate)
    return (
      <AdasBox className='flex flex-col gap-4'>
        <StatusBanner
          tone='amber'
          label={tier.name}
          title={`${tier.name} — ${daysLeft} ${daysLeft === 1 ? 'day' : 'days'} left.`}
          body={`It ends on ${formatBillingDate(sub.trialEndDate)}; after that everything is locked until you choose a plan. Subscribe any time — plans are billed monthly from the day you subscribe.`}
          action={
            <DarkButton onClick={() => onCheckout?.(buyTier.id)} disabled={pending}>
              {pending ? 'Opening…' : `Subscribe to ${buyTier.name}`}
            </DarkButton>
          }
        />
        <CurrentPlanCard
          tier={tier}
          title={`Your ${tier.name}`}
          status={{ label: 'Trialing', tone: 'amber' }}
          priceLabel={`${formatPrice(tier.priceCents)} one-time · ${tier.durationDays} days`}
          footer={<PlanRenewalFooter label='Ends' value={formatBillingDate(sub.trialEndDate)} />}
        />
        <PlanComparison {...comparisonProps} />
        <InvoicesCard invoices={invoices} tierName={tier.name} />
      </AdasBox>
    )
  }

  if (state === 'trialing') {
    const daysLeft = daysUntil(sub.trialEndDate)
    const trialEnds = formatBillingDate(sub.trialEndDate)
    const onFile = stripeBacked ? describePaymentMethod(sub) : null // card, Link wallet or bank account
    const hasCard = Boolean(onFile)
    // Free trials are frozen (backend billing.trials.frozen): the location is locked until it pays.
    if (sub.trialPaused) {
      // The Free Trial package is not a plan to "start": what they buy next is a monthly plan.
      const pausedBody = onTrialPackage
        ? `Subscribe to ${buyTier.name} to unlock everything again — billed monthly from today. Your data is kept.`
        : !stripeBacked
          ? `Subscribe to start your ${tier.name} plan today — your first month is billed right away and everything unlocks.`
          : hasCard
            ? `Start your ${tier.name} plan now: your ${onFile} is charged ${formatPrice(tier.priceCents)} today and everything unlocks right away.`
            : 'Add a payment method, then start your plan to unlock everything again.'
      const pausedAction = !stripeBacked ? (
        <DarkButton onClick={() => onCheckout?.(buyTier.id)} disabled={pending}>
          {pending ? 'Opening…' : onTrialPackage ? `Subscribe to ${buyTier.name}` : 'Subscribe now'}
        </DarkButton>
      ) : hasCard ? (
        <DarkButton onClick={() => onEndTrial?.()} disabled={pending}>
          {pending ? 'Starting…' : 'Start paying now'}
        </DarkButton>
      ) : (
        <DarkButton onClick={onManageBilling} disabled={pending}>
          {pending ? 'Opening…' : PORTAL.addLabel}
        </DarkButton>
      )
      return (
        <AdasBox className='flex flex-col gap-4'>
          <StatusBanner
            tone='red'
            label='Trial paused'
            title='Trials are paused — this location is locked until it subscribes.'
            body={pausedBody}
            action={pausedAction}
          />
          <CurrentPlanCard
            payment={hasCard && paymentInline}
            tier={tier}
            title={onTrialPackage ? `Your ${tier.name}` : 'Your trial plan'}
            status={{ label: 'Paused', tone: 'red' }}
            action={compareLink}
            priceLabel={
              onTrialPackage
                ? `${formatPrice(tier.priceCents)} one-time · ${tier.durationDays} days`
                : perLocation(tier) || 'Billed once you subscribe'
            }
            footer={<PlanRenewalFooter label='Trial was due to end' value={trialEnds} />}
          />
          {stripeBacked && <InvoicesCard invoices={invoices} tierName={tier.name} />}
        </AdasBox>
      )
    }
    // A promo code's free period (a Stripe trial on the plan): no charge until it ends, then the normal price.
    if (sub.promo && stripeBacked) {
      return (
        <AdasBox className='flex flex-col gap-4'>
          <StatusBanner
            tone='green'
            label='Promo'
            title={`Free with promo code ${sub.promo.code} — ${daysLeft} ${daysLeft === 1 ? 'day' : 'days'} left.`}
            body={
              hasCard
                ? `Your ${tier.name} plan is free until ${trialEnds}. Your ${onFile} will be charged ${formatPrice(tier.priceCents)} on that date, then monthly. Cancel before then in Manage billing and you won’t be charged.`
                : `Your ${tier.name} plan is free until ${trialEnds}. Add a payment method to keep access when it ends.`
            }
            action={
              hasCard ? null : (
                <DarkButton onClick={onManageBilling} disabled={pending}>
                  {pending ? 'Opening…' : PORTAL.addLabel}
                </DarkButton>
              )
            }
          />
          <CurrentPlanCard
            payment={hasCard && paymentInline}
            tier={tier}
            title='Current Plan'
            status={{ label: 'Free period', tone: 'green' }}
            action={compareLink}
            priceLabel={`Free until ${trialEnds} · then ${perLocation(tier)}`}
            footer={<PlanRenewalFooter label='First charge' value={trialEnds} />}
          />
          <AddOnsCard addonKeys={sub.addons} />
          <InvoicesCard invoices={invoices} tierName={tier.name} />
        </AdasBox>
      )
    }
    // An intro period ("$1 for the first 30 days", 0173) is a paid trial on the plan; the plan's price starts when it ends.
    const intro = sub.introDays > 0
    const afterIntro = sub.discount
      ? `${formatPrice(sub.discount.discountedPriceCents)} (${discountLine({ discount: sub.discount })})`
      : formatPrice(tier.priceCents)
    const body = !stripeBacked
      ? `Your trial ends on ${trialEnds}. Subscribe now to start your plan today — your first month is billed right away and the trial ends.`
      : hasCard
        ? intro
          ? `You’re all set. Your intro period ends on ${trialEnds}; your ${onFile} will be charged ${afterIntro} on that date, then every month.`
          : `You’re all set. Your trial ends on ${trialEnds}; your ${onFile} will be charged ${formatPrice(tier.priceCents)} on that date.`
        : `Your ${intro ? 'intro period' : 'trial'} ends on ${trialEnds}. Add a payment method to keep access when it ends.`
    // With a payment method on file the card below carries "Update payment method"; the banner
    // then needs no button (nothing to do).
    const action = !stripeBacked ? (
      <DarkButton onClick={() => onCheckout?.(buyTier.id)} disabled={pending}>
        {pending ? 'Opening…' : 'Subscribe now'}
      </DarkButton>
    ) : hasCard ? null : (
      <DarkButton onClick={onManageBilling} disabled={pending}>
        {pending ? 'Opening…' : PORTAL.addLabel}
      </DarkButton>
    )
    return (
      <AdasBox className='flex flex-col gap-4'>
        <StatusBanner
          tone='amber'
          label={intro ? 'Intro period' : 'Trial'}
          title={`${intro ? 'Intro period' : 'Trial'} — ${daysLeft} ${daysLeft === 1 ? 'day' : 'days'} left.`}
          body={body}
          action={action}
        />
        <CurrentPlanCard
          payment={hasCard && paymentInline}
          tier={tier}
          title={intro ? 'Your plan' : 'Your trial plan'}
          status={{ label: 'Trialing', tone: 'amber' }}
          action={compareLink}
          priceLabel={
            intro
              ? `${sub.introFeeCents != null ? formatPrice(sub.introFeeCents) : 'Intro'} for the first ${sub.introDays} days · then ${afterIntro} / location / mo`
              : tier.priceCents != null
                ? `Trial period · then ${perLocation(tier)}`
                : 'Trial period'
          }
          footer={<PlanRenewalFooter label={intro ? 'Intro period ends' : 'Trial ends'} value={formatBillingDate(sub.trialEndDate)} />}
        />
        <AddOnsCard addonKeys={sub.addons} />
        {stripeBacked && <InvoicesCard invoices={invoices} tierName={tier.name} />}
      </AdasBox>
    )
  }

  // 3 · Active — the steady state.
  const planChange = sub.planChange
  return (
    <AdasBox className='flex flex-col gap-4'>
      {planChange?.status === 'PENDING' && (
        <StatusBanner
          tone='amber'
          label='Downgrade in progress'
          title={`You asked to move to ${planChange.toTierName}. Our team will review it shortly.`}
          body={`Nothing changes until it's approved — then you keep ${planChange.fromTierName} until the end of the period you've paid for.`}
          action={
            <OutlineButton onClick={() => onCancelDowngrade?.(planChange.id)} disabled={pending}>
              Cancel downgrade
            </OutlineButton>
          }
        />
      )}
      {planChange?.status === 'SCHEDULED' && (
        <StatusBanner
          tone='slate'
          label='Plan change scheduled'
          title={`Your plan changes to ${planChange.toTierName} on ${formatBillingDate(planChange.effectiveAt)}.`}
          body={`Approved by our team. You keep ${planChange.fromTierName} until then; the lower price applies from that date. To keep your plan, contact support.`}
        />
      )}
      {sub.cancelAtPeriodEnd && (
        <StatusBanner
          tone='slate'
          label='Scheduled'
          title={`Your subscription is scheduled to cancel on ${formatBillingDate(sub.currentPeriodEnd)}.`}
          body='Access continues until then. You can resume it from the billing portal.'
          action={
            <OutlineButton onClick={onManageBilling} disabled={pending}>
              {pending ? 'Opening…' : 'Open billing portal'}
            </OutlineButton>
          }
        />
      )}
      <CurrentPlanCard
        payment={paymentInline}
        tier={tier}
        action={compareLink}
        status={sub.cancelAtPeriodEnd ? { label: 'Canceling', tone: 'amber' } : { label: 'Active', tone: 'green' }}
        footer={
          <PlanRenewalFooter label={sub.cancelAtPeriodEnd ? 'Access until' : 'Renews on'} value={formatBillingDate(sub.currentPeriodEnd)} />
        }
      />
      <AddOnsCard addonKeys={sub.addons} />
      <InvoicesCard invoices={invoices} tierName={tier.name} />
      <ManagePlanCard
        catalog={catalog}
        tier={tier}
        pending={pending}
        onManageBilling={onManageBilling}
        onUpgrade={onUpgrade}
        onDowngrade={onDowngrade}
        planChange={planChange}
        onCancelDowngrade={onCancelDowngrade}
      />
    </AdasBox>
  )
}
SubscriptionOverview.propTypes = {
  catalog: PropTypes.shape({ tiers: PropTypes.array, modules: PropTypes.array }),
  state: PropTypes.oneOf(['network', 'none', 'trialing', 'active', 'past_due', 'canceled']).isRequired,
  subscription: PropTypes.object,
  invoices: PropTypes.array,
  onCompare: PropTypes.func,
  onContact: PropTypes.func,
  onManageBilling: PropTypes.func,
  onCheckout: PropTypes.func,
  onEndTrial: PropTypes.func,
  onUpgrade: PropTypes.func,
  onCancelDowngrade: PropTypes.func,
  onDowngrade: PropTypes.func,
  pending: PropTypes.bool
}
